import { forwardRef, useCallback } from "react";
import { createLink } from "@tanstack/react-router";
import classnames from "classnames";

import { EventNames, useTracking } from "lib/tracking";
import TextButton from "./TextButton";

import css from "./Links.module.scss";

// Styling/tracking/auth props layered on top of TanStack's Link props. createLink
// forwards these through to the anchor below, so call sites can pass them
// alongside the strictly-typed `to`/`params`/`search`.
type RouterTextLinkInnerProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "children"> & {
  eventName?: EventNames;
  eventAttributes?: Record<string, string | string[] | Record<string, string[]> | undefined | null>;
  secondary?: boolean;
  ellipsis?: boolean;
  // When false, render a button (e.g. a paywall trigger) instead of navigating.
  authorised?: boolean;
  onUnauthorisedClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  children?: React.ReactNode;
};

// The anchor createLink wraps: createLink resolves `to`/`params`/`search` into an
// href + a navigating onClick, then renders this. We layer styling, event
// tracking and the unauthorised-button fallback on top here.
const RouterTextLinkInner = forwardRef<HTMLAnchorElement, RouterTextLinkInnerProps>(
  function RouterTextLinkInner(
    {
      className,
      eventName,
      eventAttributes,
      secondary = false,
      ellipsis = false,
      authorised = true,
      onUnauthorisedClick,
      onClick,
      children,
      ...anchorProps
    },
    ref,
  ) {
    const { logEvent } = useTracking();

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        if (eventName) logEvent(eventName, eventAttributes);
        if (onClick) onClick(e);
        e.stopPropagation();
      },
      [eventName, eventAttributes, logEvent, onClick],
    );

    const baseClassName = secondary ? css.secondaryTextLink : css.textLink;
    const combinedClassName = classnames(className, baseClassName, { [css.ellipsis]: ellipsis });

    if (!authorised) {
      return (
        <TextButton className={combinedClassName} onClick={onUnauthorisedClick}>
          {children}
        </TextButton>
      );
    }

    return (
      <a ref={ref} className={combinedClassName} {...anchorProps} onClick={handleClick}>
        {children}
      </a>
    );
  },
);

// Why createLink + an inner component rather than a plain wrapper around <Link>:
// we want ONE shared link (styling + tracking + paywall fallback) that KEEPS
// <Link>'s strict `to`/`params`/`search` typing. Hand-typing a wrapper degrades
// `to` to a loose string; createLink is what preserves the generics through the
// wrapper, so `<RouterTextLink to="/leads/$id" params={{ id }} />` makes a bad
// route a compile error. (Using <Link> directly would also type-check, but then
// every call site re-implements the styling/tracking/paywall.) Same pattern as
// Button/Card.
export const RouterTextLink = createLink(RouterTextLinkInner);

export type RouterTextLinkProps = React.ComponentProps<typeof RouterTextLink>;

// Loose object form of a router-link config, for passing the target as a PROP
// rather than as JSX (e.g. EllipsisTooltipRouterLink's `linkProps`). TanStack
// can't infer the strict `to`→`params` relationship through a prop object the
// way it does inline in JSX, so this stays loose and the target is validated at
// the source instead — `appRoutes` entries are `satisfies LinkProps`, so a bad
// route is still caught there.
export type RouterTextLinkOptions = Omit<RouterTextLinkInnerProps, "children"> & {
  to: string;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
};
